import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {DEFAULT_PLATE_SIZE, PLATE_SIZE_OPTIONS} from 'notebook/tables/TableConstants';
import {useCloseableDropdownContext} from 'ui-library/dropdowns/CloseableDropdownContext';
import Option from 'ui-library/menus/Option';

interface InsertWellPlateMenuProps {
  insertWellPlateWithDimensions: (numRows: number, numCols: number) => void;
}

/**
 * A light-weight React component that renders a menu for selecting well-plate dimensions.
 * The default plate size starts highlighted.
 */

export default function InsertWellPlateMenu({
  insertWellPlateWithDimensions,
}: InsertWellPlateMenuProps): JSX.Element {
  const {onCloseDropdown} = useCloseableDropdownContext();
  const [hasHovered, setHasHovered] = React.useState(false);

  const getPlateSizeOptions = (): Array<[number, number]> => {
    const customSizes: Array<[number, number]> =
      BenchlingConfig.INVENTORY_CUSTOM_PLATE_SIZES?.map((customSize: {rows: number; columns: number}) => [
        customSize.rows,
        customSize.columns,
      ]) || [];

    const combined: Array<[number, number]> = [...customSizes, ...PLATE_SIZE_OPTIONS] as Array<
      [number, number]
    >;

    return combined.sort((sizeA, sizeB) => {
      const totalA = sizeA[0] * sizeA[1];
      const totalB = sizeB[0] * sizeB[1];
      return totalB - totalA; // Sort by total size descending
    });
  };

  const plateSizeOptions = getPlateSizeOptions();

  return (
    <>
      {plateSizeOptions.map(([y, x], i) => (
        <Option
          key={i}
          value={[y, x]}
          isHovered={
            !hasHovered && y === DEFAULT_PLATE_SIZE[0] && x === DEFAULT_PLATE_SIZE[1] ? true : undefined
          }
          onHover={() => {
            setHasHovered(true);
          }}
          onSelect={() => {
            insertWellPlateWithDimensions(y, x);
            onCloseDropdown();
          }}
          disableEventPropagation={true}
        >
          {`${x * y}-well plate (${y} x ${x})`}
        </Option>
      ))}
    </>
  );
}
